// src/pages/announcements/announcementList/announcementList.js
import React, { useEffect, useReducer, useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Table, Pagination, Modal, DatePicker, Button, Tooltip } from "antd";
import { FaEdit, FaTrash } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./announcementList.css";

const { RangePicker } = DatePicker;

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  announcementData: [],
  currentPage: 1,
  pageSize: 10,
  sortConfig: null,
  showDateRangeModal: false,
  tempFromDate: null,
  tempToDate: null,
  fromDate: null,
  toDate: null
};

// Reducer for state management
const announcementReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setAnnouncementData":
      return { ...state, announcementData: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload, currentPage: 1 };
    case "setDateRangeModal":
      return { ...state, showDateRangeModal: action.payload };
    case "setTempDateRange":
      return {
        ...state,
        tempFromDate: action.payload.from,
        tempToDate: action.payload.to
      };
    case "setDateRange":
      return {
        ...state,
        fromDate: action.payload.from,
        toDate: action.payload.to
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1,
        fromDate: null,
        toDate: null,
        tempFromDate: null,
        tempToDate: null,
        showDateRangeModal: false
      };
    default:
      return state;
  }
};

function AnnouncementList() {
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(announcementReducer, initialState);
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");

  const { isLoading, searchTerm, filters, announcementData, currentPage, pageSize, sortConfig, showDateRangeModal, tempFromDate, tempToDate, fromDate, toDate } = state;

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      publishDate: {
        label: "Publish Date",
        options: [
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "dateRange", label: "Custom Date Range" }
        ]
      },
      category: {
        label: "Category",
        options: [
          { value: "general", label: "General" },
          { value: "worship", label: "Worship" },
          { value: "events", label: "Events" },
          { value: "ministry", label: "Ministry" },
          { value: "prayer", label: "Prayer" },
          { value: "community", label: "Community" }
        ]
      },
      deliveryStatus: {
        label: "Delivery Status",
        options: [
          { value: "sent", label: "Sent" },
          { value: "draft", label: "Draft" },
          { value: "scheduled", label: "Scheduled" }
        ]
      }
    }),
    []
  );

  const mappings = useMemo(
    () => ({
      categoryLabels: {
        general: "General",
        worship: "Worship",
        events: "Events",
        ministry: "Ministry",
        prayer: "Prayer",
        community: "Community"
      },
      statusLabels: {
        sent: "Sent",
        draft: "Draft",
        scheduled: "Scheduled"
      }
    }),
    []
  );

  // Utility functions for status colors
  const getStatusColors = useCallback((status) => {
    const colorMap = {
      Sent: { bg: "#C8E6C9", text: "#2E7D32" },
      Draft: { bg: "#FFCDD2", text: "#C62828" },
      Scheduled: { bg: "#E1BEE7", text: "#6A1B9A" }
    };
    return colorMap[status] || { bg: "#ECEFF1", text: "#455A64" };
  }, []);

  // Date filtering utility
  const filterByDate = useCallback(
    (items, dateFilter) => {
      if (!dateFilter) return items;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (dateFilter) {
        case "last7Days": {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return items.filter((item) => new Date(item.publishDate || item.createdAt || item.created_at) >= sevenDaysAgo);
        }
        case "last30Days": {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return items.filter((item) => new Date(item.publishDate || item.createdAt || item.created_at) >= thirtyDaysAgo);
        }
        case "dateRange": {
          if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            return items.filter((item) => {
              const date = new Date(item.publishDate || item.createdAt || item.created_at);
              return date >= from && date <= to;
            });
          }
          return items;
        }
        default:
          return items;
      }
    },
    [fromDate, toDate]
  );

  // Global search function
  const globalSearch = useCallback(
    (items, term) => {
      if (!term?.trim()) return items;

      const searchLower = term.toLowerCase().trim();

      return items.filter((item) => {
        const category = mappings.categoryLabels[item.category] || item.category || "";

        const publishDate =
          item.publishDate || item.createdAt || item.created_at
            ? new Date(item.publishDate || item.createdAt || item.created_at)
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "";

        const status = mappings.statusLabels[item.notificationStatus] || item.notificationStatus || "";

        const searchFields = [item.title || "", category, publishDate, status];

        return searchFields.some((field) => field.toLowerCase().includes(searchLower));
      });
    },
    [mappings]
  );

  // Apply all filters, search, and sorting
  const filteredData = useMemo(() => {
    let result = [...announcementData];

    result = filterByDate(result, filters.publishDate);

    if (filters.category) {
      result = result.filter((item) => item.category === filters.category);
    }

    if (filters.deliveryStatus) {
      result = result.filter((item) => item.notificationStatus === filters.deliveryStatus);
    }

    result = globalSearch(result, searchTerm);

    if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;

        if (key === "publishDate") {
          const dateA = new Date(a.publishDate || a.createdAt || a.created_at);
          const dateB = new Date(b.publishDate || b.createdAt || b.created_at);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (key === "category") {
          const catA = mappings.categoryLabels[a.category] || a.category || "";
          const catB = mappings.categoryLabels[b.category] || b.category || "";
          return direction === "asc" ? catA.localeCompare(catB) : catB.localeCompare(catA);
        }

        if (key === "notificationStatus") {
          const statusA = mappings.statusLabels[a.notificationStatus] || a.notificationStatus || "";
          const statusB = mappings.statusLabels[b.notificationStatus] || b.notificationStatus || "";
          return direction === "asc" ? statusA.localeCompare(statusB) : statusB.localeCompare(statusA);
        }

        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";

        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [announcementData, filters, searchTerm, filterByDate, globalSearch, sortConfig, mappings]);

  // Paginated data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Debounced search
  const debouncedDispatch = useMemo(
    () =>
      debounce((term) => {
        dispatch({ type: "setSearchTerm", payload: term });
      }, 300),
    []
  );

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedDispatch(term);
    },
    [debouncedDispatch]
  );

  const handleFilterChange = useCallback(
    (newFilters) => {
      if (newFilters.publishDate === "dateRange" && filters.publishDate !== "dateRange") {
        dispatch({ type: "setDateRangeModal", payload: true });
        return;
      }

      if (filters.publishDate === "dateRange" && newFilters.publishDate !== "dateRange") {
        dispatch({ type: "setDateRange", payload: { from: null, to: null } });
        dispatch({
          type: "setTempDateRange",
          payload: { from: null, to: null }
        });
      }

      dispatch({ type: "setFilters", payload: newFilters });
    },
    [filters]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
    setDisplaySearchTerm("");
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // Navigate to form page with no state = create mode
  const handleCreateClick = useCallback(() => {
    navigate("/announcementDetails");
  }, [navigate]);

  // Navigate to form page with record as state = edit mode
  const handleEdit = useCallback(
    (record) => {
      navigate("/announcementDetails", { state: record });
    },
    [navigate]
  );

  // Delete directly from the list with API call
  const handleDelete = useCallback(
    async (record) => {
      if (!window.confirm("Are you sure you want to delete this announcement?")) return;

      try {
        const token = await getAuthToken();
        await axios.delete(API_ENDPOINTS.NOTIFICATIONS, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          data: {
            id: record.id || record.announcement_id,
            title: record.title
          }
        });

        showNotification(`Announcement "${record.title}" deleted successfully.`, "success");

        // Remove deleted record from local state without refetching
        dispatch({
          type: "setAnnouncementData",
          payload: announcementData.filter((item) => (item.id || item.announcement_id) !== (record.id || record.announcement_id))
        });
      } catch (error) {
        console.error("Delete failed:", error);
        showNotification("Failed to delete announcement: " + (error.response?.data?.message || error.message), "error");
      }
    },
    [announcementData, showNotification]
  );

  const handleDateRangeChange = useCallback((dates, dateStrings) => {
    dispatch({
      type: "setTempDateRange",
      payload: {
        from: dateStrings[0] || null,
        to: dateStrings[1] || null
      }
    });
  }, []);

  const applyDateRangeFilter = useCallback(() => {
    if (tempFromDate && tempToDate) {
      dispatch({
        type: "setDateRange",
        payload: { from: tempFromDate, to: tempToDate }
      });
      dispatch({
        type: "setFilters",
        payload: { ...filters, publishDate: "dateRange" }
      });
      dispatch({ type: "setDateRangeModal", payload: false });
    } else {
      alert("Please select both from and to dates");
    }
  }, [tempFromDate, tempToDate, filters]);

  const handleDateRangeModalCancel = useCallback(() => {
    dispatch({ type: "setDateRangeModal", payload: false });
    dispatch({ type: "setTempDateRange", payload: { from: null, to: null } });
    const updatedFilters = { ...filters };
    delete updatedFilters.publishDate;
    dispatch({ type: "setFilters", payload: updatedFilters });
  }, [filters]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter.order) {
      dispatch({
        type: "setSortConfig",
        payload: {
          key: sorter.field,
          direction: sorter.order === "ascend" ? "asc" : "desc"
        }
      });
    } else {
      dispatch({ type: "setSortConfig", payload: null });
    }
  }, []);

  // Table columns
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "TITLE",
        dataIndex: "title",
        sorter: true,
        sortOrder: sortConfig?.key === "title" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (title) => title || "-"
      },
      {
        title: "CATEGORY",
        dataIndex: "category",
        sorter: true,
        sortOrder: sortConfig?.key === "category" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (category) => mappings.categoryLabels[category] || category || "-"
      },
      {
        title: "DATE",
        dataIndex: "publishDate",
        sorter: true,
        sortOrder: sortConfig?.key === "publishDate" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => {
          const date = record.publishDate || record.createdAt || record.created_at;
          return date
            ? new Date(date)
                .toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "-";
        }
      },
      {
        title: "DELIVERY STATUS",
        dataIndex: "notificationStatus",
        sorter: true,
        sortOrder: sortConfig?.key === "notificationStatus" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => {
          const status = mappings.statusLabels[record.notificationStatus] || record.notificationStatus;
          const colors = getStatusColors(status);

          return (
            <span
              className="aml-status-badge"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
                padding: "4px 8px",
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: "500"
              }}
            >
              {status || "-"}
            </span>
          );
        }
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="aml-action-buttons" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Edit">
              <button className="aml-table-edit-btn" onClick={() => handleEdit(record)}>
                <FaEdit />
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button className="aml-table-delete-btn" onClick={() => handleDelete(record)}>
                <FaTrash />
              </button>
            </Tooltip>
          </div>
        ),
        width: 120
      }
    ],
    [mappings, getStatusColors, handleEdit, handleDelete, currentPage, pageSize, sortConfig]
  );

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((announcement) => ({
      "Announcement ID": announcement.id || announcement.announcement_id,
      Title: announcement.title,
      Category: mappings.categoryLabels[announcement.category] || announcement.category,
      "Publish Date": announcement.publishDate || announcement.createdAt || announcement.created_at,
      "Delivery Status": mappings.statusLabels[announcement.notificationStatus] || announcement.notificationStatus
    }));
  }, [filteredData, mappings]);

  // Fetch announcements on mount
  useEffect(() => {
    let isMounted = true;

    const fetchAnnouncements = async () => {
      try {
        dispatch({ type: "setLoading", payload: true });

        const token = await getAuthToken();
        const response = await axios.get(API_ENDPOINTS.NOTIFICATIONS, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          const items = response.data?.announcements?.Items || response.data?.announcements || response.data?.Items || response.data || [];
          dispatch({ type: "setAnnouncementData", payload: items });
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
        if (isMounted) {
          showNotification("Failed to load announcements", "error");
          dispatch({ type: "setAnnouncementData", payload: [] });
        }
      } finally {
        if (isMounted) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    };

    fetchAnnouncements();

    return () => {
      isMounted = false;
    };
  }, [showNotification]);

  return (
    <Layout
      navTitle="Announcements"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search across all fields..."
            searchTerm={displaySearchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="announcement-list"
            excelSheetName="Announcements"
            extraButtons={[
              <Button className="aml-ttb-extra-btn" key="create-announcement" onClick={handleCreateClick}>
                Create New
              </Button>
            ]}
          />
        }
      >
        <Table
          className="aml-table"
          rowKey={(record) => record.id || record.announcement_id}
          rowClassName={(_, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{ showExpandColumn: false }}
          onChange={handleTableChange}
        />
        <div className="aml-pagination-wrapper">
          <Pagination className="aml-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      <Modal
        title="Select Date Range"
        open={showDateRangeModal}
        onCancel={handleDateRangeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDateRangeModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" onClick={applyDateRangeFilter}>
            Apply Filter
          </Button>
        ]}
        width={450}
        centered={false}
        style={{ top: 120 }}
      >
        <div style={{ marginBottom: "20px" }}>
          <RangePicker
            style={{ width: "100%" }}
            value={tempFromDate && tempToDate ? [dayjs(tempFromDate), dayjs(tempToDate)] : []}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["From Date", "To Date"]}
          />
        </div>
      </Modal>
    </Layout>
  );
}

export default AnnouncementList;
